import { Button } from "web-design-system";
import { ClipboardListIcon, ClockIcon, ShieldCheckIcon } from "lucide-react";
import { riskProfileShieldsImg } from "web-core";
import { handleError } from "web-core/utils";
import { toast } from "sonner";
import { useCallback } from "react";
import { useSendRiskProfileQuestionnare } from "creso-core/hooks";
import ThrottledButton from "@core/components/throttledButton";

const INFO_ROWS = [
  {
    icon: ClipboardListIcon,
    title: "6 quick questions",
    description: "Time horizon, income stability and market comfort",
  },
  {
    icon: ClockIcon,
    title: "Takes under a minute",
    description: "Answers are saved to the investor's profile instantly",
  },
  {
    icon: ShieldCheckIcon,
    title: "One of three profiles",
    description: "Conservative, Moderate or Aggressive",
  },
];

export function IntroStep({
  onProceed,
  clientId,
}: {
  onProceed: () => void;
  clientId: number;
}) {
  const { mutateAsync: sendRiskProfileLink } = useSendRiskProfileQuestionnare();

  const onShareRiskProfileLink = useCallback(async () => {
    try {
      await sendRiskProfileLink(clientId);
      toast.success("Risk profile link sent to the client.");
    } catch (e) {
      handleError(e, "Failed to send link.");
    }
  }, [clientId, sendRiskProfileLink]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6">
        <div className="flex flex-col items-center gap-6 rounded-lg bg-linear-to-r from-card to-secondary p-8">
          <img src={riskProfileShieldsImg} alt="" className="h-24 w-auto" />
          <p className="text-center font-medium text-foreground">
            Answer a few questions to gauge the investor&apos;s risk appetite
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {INFO_ROWS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="flex items-center gap-4 rounded-lg border border-border p-4"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-linear-to-r from-card to-secondary">
                <Icon className="size-4 text-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">
                  {title}
                </span>
                <span className="text-xs text-muted-foreground">
                  {description}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-9 flex flex-row items-center gap-3">
        <ThrottledButton
          className="flex-1"
          variant="outline"
          throttle={25}
          beginOnMount={false}
          onClick={onShareRiskProfileLink}
        >
          Send Link
        </ThrottledButton>
        <Button className="flex-1" onClick={onProceed}>
          Proceed
        </Button>
      </div>
    </div>
  );
}
